import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import CaptchaPlaceholder from '../../components/common/CaptchaPlaceholder';
// Custom styled components
const StyledCard = ({ children }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '400px'
  }}>{children}</div>
);

const StyledInput = ({ label, type = 'text', value, onChange, error }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', marginBottom: '0.5rem' }}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '0.75rem',
        border: `1px solid ${error ? 'red' : '#ccc'}`,
        borderRadius: '4px',
        fontSize: '1rem'
      }}
    />
    {error && <span style={{ color: 'red', fontSize: '0.875rem' }}>{error}</span>}
  </div>
);

const StyledButton = ({ children, onClick, disabled, type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      width: '100%',
      padding: '0.75rem',
      backgroundColor: '#1976d2',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '1rem',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.7 : 1
    }}
  >
    {children}
  </button>
);

const StyledAlert = ({ type, children }) => (
  <div style={{
    padding: '0.75rem',
    marginBottom: '1rem',
    borderRadius: '4px',
    backgroundColor: type === 'error' ? '#fdeded' : '#edf7ed',
    color: type === 'error' ? '#5f2120' : '#1e4620',
    border: `1px solid ${type === 'error' ? '#ef5350' : '#4caf50'}`
  }}>
    {children}
  </div>
);

const LoadingSpinner = () => (
  <div style={{
    display: 'inline-block',
    width: '1.5rem',
    height: '1.5rem',
    border: '3px solid rgba(255,255,255,0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 1s linear infinite'
  }}></div>
);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setEmailError('');
    setPasswordError('');

    // Validasi
    let isValid = true;
    if (!email) {
      setEmailError('Email harus diisi');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Email tidak valid');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password harus diisi');
      isValid = false;
    }

    if (!isValid) {
      setLoading(false);
      return;
    }

    try {
      // Login dengan Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('User authenticated:', user.uid);

      // Cek data user di Firestore
      const userRef = doc(db, 'users', user.uid);
      console.log('Checking Firestore document:', userRef.path);
      
      const userDoc = await getDoc(userRef);
      console.log('Firestore response:', userDoc.exists() ? 'Document exists' : 'Document not found');
      
      if (!userDoc.exists()) {
        // Coba cek collection users
        console.log('Document not found, creating new user document...');
        try {
          // Buat dokumen baru dengan role default
          await setDoc(userRef, {
            email: user.email,
            role: 'mahasiswa', // Default role
            approved: false, // Use 'approved' field
            createdAt: new Date().toISOString()
          });
          setError('Akun berhasil dibuat, menunggu persetujuan admin');
          return;
        } catch (err) {
          console.error('Error creating user document:', err);
          setError('Gagal membuat data user');
          return;
        }
      }

      const userData = userDoc.data();
      // Log the entire userData object to inspect its contents
      console.log('User data from Firestore:', userData);
      // Check the 'approved' field leniently (boolean true or string "true")
      if (!(userData.approved === true || userData.approved === "true")) {
        // Log if the approval check fails
        console.log('Approval check failed. userData.approved:', userData.approved);
        setError('Akun Anda belum disetujui oleh admin');
        return;
      }

      // Redirect based on role to the unified dashboard
      const role = userData.role;
      switch (role) {
        case 'admin':
        case 'laboran':
        case 'mahasiswa':
        case 'dosen': // Added dosen case
          navigate('/dashboard'); // Navigate all valid roles to /dashboard
          break;
        default:
          // Log the invalid role and set error
          console.error(`Invalid or unrecognized role found during login: ${role}`);
          setError('Role pengguna tidak valid atau tidak dikenali.');
          // Optionally sign out the user if role is mandatory
          // await auth.signOut();
          return;
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
      // Set error spesifik berdasarkan pesan error Firebase
      if (err.code === 'auth/user-not-found') {
        setEmailError('Email tidak terdaftar');
      } else if (err.code === 'auth/wrong-password') {
        setPasswordError('Password salah');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      backgroundColor: '#f5f5f5',
      padding: '1rem'
    }}>
      <StyledCard>
        <h1 style={{ 
          fontSize: '1.5rem', 
          textAlign: 'center',
          marginBottom: '1.5rem',
          color: '#1976d2'
        }}>Sistem Manajemen Laboratorium Rekayasa Kosmetik</h1>

        {error && <StyledAlert type="error">{error}</StyledAlert>}
        
        <form onSubmit={handleLogin}>
          <StyledInput
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError}
          />
          
          <StyledInput
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError}
          />
          
          <StyledButton type="submit" disabled={loading}>
            {loading ? <LoadingSpinner /> : 'Login'}
          </StyledButton>
        </form>

        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <Link 
            to="/forgot-password"
            style={{
              color: '#1976d2',
              textDecoration: 'none',
              fontSize: '0.875rem',
              display: 'block',
              marginBottom: '0.5rem'
            }}
          >Lupa Password?</Link>
          <Link 
            to="/register"
            style={{
              color: '#1976d2',
              textDecoration: 'none',
              fontSize: '0.875rem'
            }}
          >Belum punya akun? Daftar</Link>
        </div>
      </StyledCard>
    </div>
  );
};

export default LoginPage;
